import { apiClient } from '@/services/authApi';
import { endpoints } from '@/services/endpoints';
import { Role } from '@/types/auth';
import {
  BulkUserUploadResult,
  CreateUserData,
  UpdateUserData,
  UserListItem,
} from '@/types/user';

// Backend types
interface UserResponse {
  _id?: string;
  userId: string;
  tenantId?: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  role?: string;
  registrationStatus?: 'pending' | 'completed';
  lastLogin?: string | null;
  referralCode?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface PaginatedUsersResponse {
  users: UserResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface BulkRegistrationResponse {
  total: number;
  successful: number;
  failed: number;
  errors?: Array<{ row: number; userId: string; error: string }>;
  createdUsers?: Array<{
    userId: string;
    firstName?: string;
    middleName?: string | null;
    lastName?: string;
    mobile?: string;
  }>;
}

function mapUserResponseToListItem(u: UserResponse): UserListItem {
  const firstName = u.firstName ?? '';
  const middleName = u.middleName ?? undefined;
  const lastName = u.lastName ?? '';
  return {
    id: u._id ?? u.userId,
    email: u.userId,
    tenantId: u.tenantId ?? '',
    firstName,
    middleName: middleName || undefined,
    lastName,
    role: (u.role as Role) ?? 'role_user',
    status: u.registrationStatus === 'completed' ? 'active' : 'pending',
    documentsCount: 0,
    referralCode: u.referralCode ?? undefined,
    createdAt: u.createdAt ?? '',
    lastLoginAt: u.lastLogin ?? undefined,
  };
}

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: Role;
  registrationStatus?: 'pending' | 'completed';
  /** Admin cross-tenant: target tenant ID (query param tenantId) */
  tenantId?: string | null;
}

export interface GetUsersResult {
  users: UserListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * GET /api/users - paginated list (admin/superadmin). Supports tenantId for cross-tenant.
 */
export async function getUsers(params?: GetUsersParams): Promise<GetUsersResult> {
  const query: Record<string, string | number> = {
    page: params?.page ?? 1,
    limit: params?.limit ?? 25,
  };
  if (params?.search) query.search = params.search;
  if (params?.role) query.role = params.role;
  if (params?.registrationStatus) query.registrationStatus = params.registrationStatus;
  if (params?.tenantId != null && params.tenantId !== '') query.tenantId = params.tenantId;

  const response = await apiClient.get<PaginatedUsersResponse>(endpoints.users.list, {
    params: query,
  });
  const raw = response.data;
  const users = (raw.users ?? []).map(mapUserResponseToListItem);
  const pagination = raw.pagination ?? {
    page: 1,
    limit: query.limit as number,
    total: users.length,
    totalPages: 1,
  };
  return { users, pagination };
}

/**
 * GET /api/users/{userId} - supports tenantId for admin cross-tenant
 */
export async function getUserById(
  id: string,
  tenantId?: string | null
): Promise<UserListItem | null> {
  try {
    const params =
      tenantId != null && tenantId !== '' ? { tenantId } : undefined;
    const response = await apiClient.get<UserResponse>(
      endpoints.users.getById(id),
      { params }
    );
    return mapUserResponseToListItem(response.data);
  } catch {
    return null;
  }
}

/**
 * POST /api/users - create user (admin/superadmin). Supports tenantId for cross-tenant.
 */
export async function createUser(
  data: CreateUserData,
  tenantId?: string | null
): Promise<UserListItem> {
  const body = {
    userId: data.email,
    firstName: data.firstName,
    ...(data.middleName != null && data.middleName !== '' && { middleName: data.middleName }),
    lastName: data.lastName,
    role: data.role ?? 'role_user',
    ...(data.referralCode && { referralCode: data.referralCode }),
  };
  const params =
    tenantId != null && tenantId !== '' ? { tenantId } : undefined;
  const response = await apiClient.post<UserResponse>(
    endpoints.users.create,
    body,
    { params }
  );
  return mapUserResponseToListItem(response.data);
}

/**
 * PATCH /api/users/{userId} - supports tenantId for admin cross-tenant
 */
export async function updateUser(
  email: string,
  data: UpdateUserData,
  tenantId?: string | null
): Promise<UserListItem> {
  const body: Record<string, string> = {};
  if (data.firstName != null) body.firstName = data.firstName;
  if (data.middleName != null) body.middleName = data.middleName;
  if (data.lastName != null) body.lastName = data.lastName;
  if (data.mobile != null) body.mobile = data.mobile;

  const params =
    tenantId != null && tenantId !== '' ? { tenantId } : undefined;
  const response = await apiClient.patch<UserResponse>(
    endpoints.users.update(email),
    body,
    { params }
  );
  return mapUserResponseToListItem(response.data);
}

/**
 * Delete user - not supported by API; throws.
 */
export async function deleteUser(): Promise<void> {
  throw new Error('Delete user is not supported by the API');
}

/**
 * POST /api/users/{userId}/resend-verification - supports tenantId for cross-tenant
 */
export async function reinviteUser(
  id: string,
  tenantId?: string | null
): Promise<void> {
  const params =
    tenantId != null && tenantId !== '' ? { tenantId } : undefined;
  await apiClient.post(endpoints.users.resendVerification(id), undefined, {
    params,
  });
}

/**
 * POST /api/users/bulk - multipart/form-data with Excel file. Supports tenantId for cross-tenant.
 */
export async function bulkCreateUsers(
  file: File,
  tenantId?: string | null
): Promise<BulkUserUploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const params =
    tenantId != null && tenantId !== '' ? { tenantId } : undefined;
  const response = await apiClient.post<BulkRegistrationResponse>(
    endpoints.users.bulk,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    }
  );

  const data = response.data;
  return {
    success: data.successful ?? 0,
    failed: data.failed ?? 0,
    errors: (data.errors ?? []).map((e) => ({
      row: e.row,
      email: e.userId,
      error: e.error,
    })),
  };
}

/**
 * GET /api/users/roles - available roles (for dropdowns)
 */
export async function getRoles(): Promise<Array<{ value: string; label: string }>> {
  const response = await apiClient.get(endpoints.users.roles);
  return response.data ?? [];
}

export interface UserStatistics {
  total: number;
  byRole: {
    role_user: number;
    role_admin: number;
    role_superadmin: number;
  };
  byStatus: {
    active: number;
    pending: number;
    inactive: number;
  };
}

/**
 * Statistics - derived from getUsers (API has no dedicated stats endpoint)
 */
export async function getUserStatistics(): Promise<UserStatistics> {
  const { users, pagination } = await getUsers({ limit: 100 });
  const byRole = {
    role_user: 0,
    role_admin: 0,
    role_superadmin: 0,
  };
  const byStatus = {
    active: 0,
    pending: 0,
    inactive: 0,
  };
  for (const u of users) {
    if (u.role in byRole) byRole[u.role as keyof typeof byRole]++;
    if (u.status in byStatus) byStatus[u.status as keyof typeof byStatus]++;
  }
  return {
    total: pagination?.total ?? users.length,
    byRole,
    byStatus,
  };
}
